import { getErrorMessage } from "@captions-web/dev-logger";
import { Icon } from "@captions-web/icons";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { t } from "i18next";
import { ToggleGroup } from "radix-ui";
import { Fragment, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { BasicVideoPlayer } from "~/components/basic-video-player";
import { LayoutBleed } from "~/components/layouts/bleed";
import { Button, LinkButton } from "~/components/primitives/button";
import { CenteredCreateBox } from "~/components/primitives/centered-create-box";
import { checkboxStyles } from "~/components/primitives/checkbox";
import { ScrollArea } from "~/components/primitives/scroll-area";
import { AuditionEvents } from "~/lib/analytics/defs/AuditionEvents";
import { useFeatureFlag } from "~/lib/feature-flags/hooks";
import { useOnMountEffect } from "~/lib/hooks/react-helpers";
import { getErrorTypeFromNetworkError } from "~/lib/network/known-api-error";
import { PollingFailedError } from "~/lib/network/polling-helper";
import { ActorAuditionLibrary } from "~/modules/actors/components/actor-audition-library";
import { CreateActorTextDialog } from "~/modules/actors/components/create-actor-text-dialog";
import { AudioTrimmer } from "~/modules/audio/components/audio-trimmer/audio-trimmer";
import { CreditsPaywallDialog } from "~/modules/credits/components/credits-paywall";
import { ProjectOptionsMenu } from "~/modules/project/components/project-options-menu";
import { UsedActorThumbnailsList } from "~/modules/project/components/used-actor-thumbnails-list";
import {
  videoCardStyles,
  VideoItemFailed,
  VideoItemLoading,
} from "~/modules/project/components/video-item-states";
import { ProjectLayout } from "~/modules/project/layouts/project-layout";
import {
  cancelAuditionMutation,
  createAuditionsFromAudioMutation,
  createAuditionsFromAudioWithPromptMutation,
  generateVideoFromAuditionMutation,
} from "~/modules/project/network/mutations";
import {
  getProjectDetailsQuery,
  listAuditionsForAudioQuery,
  pollAuditionQuery,
} from "~/modules/project/network/queries";
import {
  AuditionFromAudioItemType,
  pollAuditionResponseSchema,
} from "~/modules/project/network/schemas";
import { getActiveVideoResult } from "~/modules/project/network/utils";
import { css, cx } from "~/styled-system/css";

const DEFAULT_AUDITIONS_COUNT = 1;

export const Route = createFileRoute("/_app/projects/$projectId/$audioId/auditions")({
  component: RouteComponent,
  validateSearch: z.object({
    fromProject: z.coerce.boolean().optional(),
  }),
  async loader({ params: { projectId }, context: { queryClient } }) {
    // prefetch the project details, we do not need to block on this
    queryClient.prefetchQuery(getProjectDetailsQuery.queryOptions({ params: { id: projectId } }));
  },
});

function RouteComponent() {
  const { audioId, projectId } = Route.useParams();
  const navigate = useNavigate();

  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  const { data: auditions } = listAuditionsForAudioQuery.useQuery({
    params: { audioId, projectId },
  });

  const { data: projectData, isPending: isFetchingProject } = getProjectDetailsQuery.useQuery({
    params: { id: projectId },
  });

  const [selectedAuditions, setSelectedAuditions] = useState<string[]>([]);

  const reversedAuditions = useMemo(() => {
    return auditions?.auditions.toReversed() ?? [];
  }, [auditions?.auditions]);

  const [showCreditsPaywall, setShowCreditsPaywall] = useState(false);

  const { mutate: startGeneratingVideos, isPending: isStartingGeneration } = useMutation({
    mutationFn: (ids: string[]) => {
      return Promise.allSettled(
        ids.map((id) =>
          generateVideoFromAuditionMutation.trigger({
            params: { auditionId: id, projectId },
          })
        )
      );
    },
    onSuccess(results, variables) {
      const allSucceeded = results.every((result) => result.status === "fulfilled");

      if (allSucceeded) {
        AuditionEvents.start_video_generation_status.track({
          status: "success",
          project_id: projectId,
          audio_id: audioId,
          audition_ids: variables,
        });

        return navigate({
          to: "/projects/$projectId",
          params: { projectId },
        });
      }

      const firstFailedResult = results.find((result) => result.status === "rejected");

      if (!firstFailedResult) {
        // this should never happen
        AuditionEvents.start_video_generation_status.track({
          status: "error",
          project_id: projectId,
          audio_id: audioId,
          audition_ids: variables,
          error_message: "BAD: No failed results",
        });
        toast.error(t("mirage-studio:auditions.errors.generate-videos"));
        return;
      }

      const firstError = firstFailedResult.reason;

      const someSucceeded = results.some((result) => result.status === "fulfilled");

      if (someSucceeded) {
        AuditionEvents.start_video_generation_status.track({
          status: "partial-success",
          project_id: projectId,
          audio_id: audioId,
          audition_ids: variables,
        });

        if (getErrorTypeFromNetworkError(firstError) === "INSUFFICIENT_CREDITS") {
          toast.warning(t("mirage-studio:auditions.warning.insufficient-credits"));
        } else if (getErrorTypeFromNetworkError(firstError) === "NUM_VIDEOS_QUEUE_EXCEEDED") {
          toast.warning(t("mirage-studio:auditions.warning.videos-queue-exceeded"));
        } else {
          toast.warning(t("mirage-studio:auditions.warning.generate-videos"));
        }

        return navigate({
          to: "/projects/$projectId",
          params: { projectId },
        });
      }

      // all the videos failed

      AuditionEvents.start_video_generation_status.track({
        status: "error",
        project_id: projectId,
        audio_id: audioId,
        audition_ids: variables,
        error_message: firstError.message,
      });

      if (getErrorTypeFromNetworkError(firstError) === "INSUFFICIENT_CREDITS") {
        setShowCreditsPaywall(true);
      } else if (getErrorTypeFromNetworkError(firstError) === "NUM_VIDEOS_QUEUE_EXCEEDED") {
        toast.error(t("mirage-studio:auditions.errors.videos-queue-exceeded"));
      } else {
        toast.error(t("mirage-studio:auditions.errors.generate-videos"));
      }
    },
    onError(error, variables) {
      // this should never be reached because we are using allSettled
      // but tracking just in case
      AuditionEvents.start_video_generation_status.track({
        status: "error",
        project_id: projectId,
        audio_id: audioId,
        audition_ids: variables,
        error_message: "BAD: onError " + error.message,
      });

      toast.error(t("mirage-studio:auditions.errors.generate-videos"));
    },
  });

  const onGenerateVideos = () => {
    if (selectedAuditions.length === 0) {
      return;
    }

    startGeneratingVideos(selectedAuditions);
  };

  const actorAuditionPreviews = useMemo(() => {
    return selectedAuditions
      .map((id) => {
        const audition = auditions?.auditions.find((audition) => audition.id === id);
        if (!audition) {
          return null;
        }

        return {
          id: audition.id,
          thumbnailUrl: audition.video.state === "COMPLETE" ? audition.video.thumbnailUrl : "",
        };
      })
      .filter(Boolean) as { id: string; thumbnailUrl: string }[];
  }, [auditions, selectedAuditions]);

  const onRemoveAudition = (id: string) => {
    setSelectedAuditions((prev) => prev.filter((audition) => audition !== id));
  };

  const { mutateAsync: createAuditionFromActor } = createAuditionsFromAudioMutation.useMutation();

  const onCreateAuditionFromActor = async (actorId: string | null) => {
    try {
      await createAuditionFromActor({
        params: {
          audioId,
          projectId,
          actorId,
          count: actorId === null ? DEFAULT_AUDITIONS_COUNT : 1,
        },
      });

      AuditionEvents.submit_actor_for_audition_status.track({
        status: "success",
        project_id: projectId,
        audio_id: audioId,
        actor_id: actorId,
      });

      AuditionEvents.created_any_audition.track({
        project_id: projectId,
        audio_id: audioId,
        type: actorId === null ? "auto-generate" : "actor",
      });
    } catch (error) {
      if (getErrorTypeFromNetworkError(error) === "INSUFFICIENT_CREDITS") {
        setShowCreditsPaywall(true);
      } else if (getErrorTypeFromNetworkError(error) === "NUM_VIDEOS_QUEUE_EXCEEDED") {
        toast.error(t("mirage-studio:auditions.errors.videos-queue-exceeded"));
      } else if (actorId) {
        toast.error(t("mirage-studio:auditions.errors.create-audition"));
      } else {
        toast.error(t("mirage-studio:auditions.errors.auto-select-auditions"));
      }

      AuditionEvents.submit_actor_for_audition_status.track({
        status: "error",
        project_id: projectId,
        audio_id: audioId,
        actor_id: actorId,

        error_message: getErrorMessage(error),
      });
    }
  };

  const { mutateAsync: createAuditionFromActorWithPrompt } =
    createAuditionsFromAudioWithPromptMutation.useMutation();

  const [isTextDialogOpen, setIsTextDialogOpen] = useState(false);

  const onCreateAuditionFromActorWithPrompt = async (
    appearanceText: string,
    backgroundText: string,
    isGreenScreen: boolean
  ) => {
    try {
      await createAuditionFromActorWithPrompt({
        params: {
          audioId,
          projectId,
          characterPrompt: appearanceText,
          backgroundPrompt: backgroundText,
          count: 1,
        },
      });

      AuditionEvents.create_audition_from_actor_with_prompt_status.track({
        status: "success",
        project_id: projectId,
        audio_id: audioId,
        character_prompt: appearanceText,
        background_prompt: backgroundText,
        is_green_screen: isGreenScreen,
      });

      AuditionEvents.created_any_audition.track({
        project_id: projectId,
        audio_id: audioId,
        type: "prompt",
      });
    } catch (error) {
      if (getErrorTypeFromNetworkError(error) === "INSUFFICIENT_CREDITS") {
        setShowCreditsPaywall(true);
      } else if (getErrorTypeFromNetworkError(error) === "NUM_VIDEOS_QUEUE_EXCEEDED") {
        toast.error(t("mirage-studio:auditions.errors.videos-queue-exceeded"));
      } else {
        toast.error(t("mirage-studio:auditions.errors.create-audition"));
      }

      AuditionEvents.create_audition_from_actor_with_prompt_status.track({
        status: "error",
        project_id: projectId,
        audio_id: audioId,
        character_prompt: appearanceText,
        background_prompt: backgroundText,
        is_green_screen: isGreenScreen,

        error_message: getErrorMessage(error),
      });
    }
  };

  const onStartedPlayingVideo = (auditionId: string) => {
    Object.entries(videoRefs.current).forEach(([id, ref]) => {
      if (id !== auditionId) {
        ref?.pause?.();
      }
    });
  };

  return (
    <Layout projectId={projectId}>
      <CreditsPaywallDialog onOpenChange={setShowCreditsPaywall} open={showCreditsPaywall} />

      <div className={containerStyles}>
        <aside className={asideStyles}>
          <ActorAuditionLibrary
            onCreateAuditionFromActor={onCreateAuditionFromActor}
            onCreateAuditionFromActorWithPrompt={onCreateAuditionFromActorWithPrompt}
          />
        </aside>
        <div className={scrollParentContainerStyles}>
          <ScrollArea className={scrollContainerStyles}>
            {auditions?.auditions.length === 0 && (
              <div className={emptyStateStyles}>
                <Icon
                  name="person-add"
                  size={48}
                  className={css({
                    color: "$gray.700",
                  })}
                />
                <h2>{t("mirage-studio:auditions.empty-state.title")}</h2>
                <p>{t("common:or")}</p>
                <CreateActorTextDialog
                  open={isTextDialogOpen}
                  onOpenChange={setIsTextDialogOpen}
                  onCreateActor={onCreateAuditionFromActorWithPrompt}
                >
                  <Button
                    variant="secondary"
                    size="lg"
                    iconPosition="before"
                    icon={<Icon name="text-shine" />}
                    onClick={() => setIsTextDialogOpen(true)}
                  >
                    {t("mirage-studio:auditions.empty-state.generate-from-text")}
                  </Button>
                </CreateActorTextDialog>
              </div>
            )}
            <ToggleGroup.Root
              type="multiple"
              value={selectedAuditions}
              onValueChange={setSelectedAuditions}
            >
              <div className={auditionsGridStyles}>
                {reversedAuditions.map((audition) => (
                  <AuditionItem
                    key={audition.id}
                    audition={audition}
                    videoRef={(ref) => {
                      videoRefs.current[audition.id] = ref;
                    }}
                    onStartedPlayingVideo={onStartedPlayingVideo}
                  />
                ))}
              </div>
            </ToggleGroup.Root>
            <CenteredCreateBox className={css({ marginX: "12px" })}>
              <div className={selectedAuditionsStyles}>
                {actorAuditionPreviews.length > 0 ? (
                  <UsedActorThumbnailsList
                    actors={actorAuditionPreviews}
                    onRemove={onRemoveAudition}
                  />
                ) : (
                  <p
                    className={css({
                      textStyle: "body-2-subtle",
                      color: "$gray.200",
                      flex: 1,
                    })}
                  >
                    {/* {t("mirage-studio:projects.audio.auditions.bar.title.generating")} */}
                    {t("mirage-studio:projects.audio.auditions.bar.title.select-one")}
                  </p>
                )}
              </div>
              <div className={boxContentStyles}>
                <AudioTrimmer
                  trimMode="none"
                  initialRegions={[]}
                  audio={projectData?.project.audios.at(-1)?.url}
                  isPending={isFetchingProject}
                />
                <Button
                  variant="primary"
                  size="md"
                  className={css({ alignSelf: "end" })}
                  disabled={selectedAuditions.length === 0 || auditions?.auditions.length === 0}
                  isPending={isStartingGeneration}
                  onClick={onGenerateVideos}
                  overBlurredSurface
                >
                  {/* not localizing for now until we do the selection logic, etc */}
                  {selectedAuditions.length < 2
                    ? t("mirage-studio:projects.audio.auditions.bar.button.select-one")
                    : t("mirage-studio:projects.audio.auditions.bar.button.select-actors", {
                        count: selectedAuditions.length,
                      })}
                </Button>
              </div>
            </CenteredCreateBox>
          </ScrollArea>
        </div>
      </div>
    </Layout>
  );
}
function Layout({ projectId, children }: { projectId: string; children: React.ReactNode }) {
  const { fromProject } = Route.useSearch();
  const { enabled: enableProjectLayout } = useFeatureFlag("studio_enable_project_nav_v2");

  const { data: projectDetails } = getProjectDetailsQuery.useQuery(
    { params: { id: projectId } },
    { staleTime: Infinity }
  );

  if (enableProjectLayout) {
    return (
      <ProjectLayout
        project={projectDetails?.project}
        subtitle={t("mirage-studio:projects.audio.auditions.subtitle-v2")}
        rightActions={<ProjectOptionsMenu projectId={projectId} />}
      >
        {children}
      </ProjectLayout>
    );
  }

  return (
    <LayoutBleed
      title={t("mirage-studio:projects.audio.auditions.title")}
      subtitle={!fromProject && t("mirage-studio:projects.audio.auditions.subtitle")}
      leftActions={
        <LinkButton
          variant="secondary"
          size="md"
          icon={<Icon name="left-arrow-small" />}
          iconPosition="only"
          to={fromProject ? "/projects/$projectId" : "/projects/$projectId/$audioId/adjust"}
        />
      }
      rightActions={<ProjectOptionsMenu projectId={projectId} />}
    >
      {children}
    </LayoutBleed>
  );
}

function AuditionItem({
  audition,
  videoRef,
  onStartedPlayingVideo,
}: {
  audition: AuditionFromAudioItemType;
  videoRef: (ref: HTMLVideoElement | null) => void;
  onStartedPlayingVideo: (auditionId: string) => void;
}) {
  const { audioId, projectId } = Route.useParams();
  const {
    begin,
    currentResult,
    error: pollingError,
  } = pollAuditionQuery.usePollingQuery({
    swallowAbortErrors: true,
    onError(error, variables) {
      let knownErrorCode: string | undefined;
      if (error instanceof PollingFailedError) {
        const parsedResult = pollAuditionResponseSchema.safeParse(error.result);
        if (parsedResult.success && parsedResult.data.video.state === "FAILED") {
          knownErrorCode = parsedResult.data.video.error?.code;
        }
      }

      AuditionEvents.generation_failed_from_client.track({
        project_id: variables.params.projectId,
        audio_id: variables.params.audioId,
        audition_id: variables.params.auditionOperationId,
        error_message: knownErrorCode ?? error.message,
      });
    },
  });

  useOnMountEffect(() => {
    if (audition.video.state === "PROCESSING" || audition.video.state === "QUEUED") {
      begin({ params: { auditionOperationId: audition.id, audioId, projectId } });
    }
  });

  const video = getActiveVideoResult(audition.video, currentResult, pollingError);

  const { mutateAsync: cancelAudition } = cancelAuditionMutation.useMutation();

  const onCancel = async () => {
    try {
      await cancelAudition({
        params: {
          auditionId: audition.id,
          projectId,
          audioId,
        },
      });

      AuditionEvents.cancel_status.track({
        status: "success",
        project_id: projectId,
        audio_id: audioId,
        audition_id: audition.id,
      });
    } catch (error) {
      toast.error(t("mirage-studio:auditions.errors.cancel-audition"));

      AuditionEvents.cancel_status.track({
        status: "error",
        project_id: projectId,
        audio_id: audioId,
        audition_id: audition.id,
        error_message: getErrorMessage(error),
      });
    }
  };

  const onPlayVideo = () => {
    onStartedPlayingVideo(audition.id);
    AuditionEvents.play.track({
      project_id: projectId,
      audio_id: audioId,
      audition_id: audition.id,
    });
  };

  if (video.state === "QUEUED" || video.state === "PROCESSING") {
    return (
      <div className={videoCardStyles()}>
        <VideoItemLoading
          progress={video.state === "PROCESSING" ? video.progress : 0}
          estimatedRemainingTimeMs={
            video.state === "PROCESSING" ? video.estimatedRemainingTimeMs : undefined
          }
          thumbnailUrl={null}
          onCancel={onCancel}
          videoItemType="audition"
        />
      </div>
    );
  }

  if (video.state === "FAILED") {
    return (
      <div className={videoCardStyles()}>
        <VideoItemFailed
          error={video.error}
          videoId={audition.id}
          projectId={projectId}
          videoItemType="audition"
        />
      </div>
    );
  }

  return (
    <div className={videoCardStyles()}>
      {video.state === "COMPLETE" && (
        <Fragment>
          <div className={headerStyles}>
            <Button
              variant="quarternary"
              tooltip={{
                content: t("mirage-studio:projects.audio.auditions.tooltip.select-for-video"),
              }}
              size="sm"
              iconPosition="only"
              icon={<Icon name="info" size={20} color="white" />}
              onClick={() => {}}
            />
            <ToggleGroup.Item value={audition.id} className={cx(checkboxStyles({ size: "md" }))}>
              <Icon name="check" size={24} />
            </ToggleGroup.Item>
          </div>
          <BasicVideoPlayer
            src={video.videoUrl}
            controls={{ play: true, progress: true }}
            theme={{ fullHeight: true }}
            onPlaying={onPlayVideo}
            ref={(ref) => videoRef(ref)}
          />
        </Fragment>
      )}
    </div>
  );
}

const containerStyles = css({
  flex: 1,
  display: "flex",
  overflow: "hidden",
});

const asideStyles = css({
  width: "300px",
  borderRight: "$thin",
  borderBottomLeftRadius: "12px",
  overflow: "hidden",
});

const scrollParentContainerStyles = css({
  flex: 1,
  position: "relative",
  display: "flex",
  flexDirection: "column",
});

const scrollContainerStyles = css({
  paddingX: "12px",
  height: "100%",
});

const auditionsGridStyles = css({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 250px))",
  justifyContent: "center",
  alignItems: "start",
  gap: "16px",
  width: "100%",
  paddingTop: "40px",
  paddingBottom: "200px",

  "@media (max-width: 1200px)": {
    gridTemplateColumns: "repeat(3, minmax(0, 250px))",
  },

  "@media (max-width: 1100px)": {
    gridTemplateColumns: "repeat(2, minmax(0, 250px))",
  },
});

const boxContentStyles = css({
  flex: 1,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const emptyStateStyles = css({
  position: "absolute",
  top: "40%",
  left: "50%",
  transform: "translate(-50%, -50%)",

  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "24px",
  maxWidth: "360px",
  textAlign: "center",

  textStyle: "heading-4",
  color: "$gray.600",
});

const headerStyles = css({
  position: "absolute",
  top: "12px",
  left: "8px",
  right: "12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  zIndex: 1,
});

const selectedAuditionsStyles = css({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
});
